import { Box, useTheme } from '@mui/material'
import { SectionHeader } from 'components/SectionHeader'
import { type LicensedEstablishmentDetailsProps } from '../types'
import { formatPhoneNumber } from 'utils/phoneUtils'
import { DataTable } from 'components/Shared/DataTable'
import { type GridEventListener, type GridColDef } from '@mui/x-data-grid'
import { orderBy } from 'lodash'
import { type LicensedEstablishmentContactType } from 'types/api'
import { DeleteLEContactTypeRenderCell } from './DeleteLEContactTypeRenderCell'
import { useState } from 'react'
import { EditLicensedEstablishmentContactDetails } from './EditLicensedEstablishmentContactDetails'
import { usePutLicensedEstablishment } from 'hooks/api/LicensedEstablishment/usePutLicensedEstablishment'
import { useSnackbar } from 'stores/useSnackbar'
import { useSetLEContactId } from 'stores/useSetLEContactId'

export const ContactDetailsDesktop = ({
  licensedEstablishment,
  onClickAdd,
}: LicensedEstablishmentDetailsProps) => {
  const theme = useTheme()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isContactDetailModalOpen, setIsContactDetailModalOpen] =
    useState(false)
  const toggleContactDetailModalIsOpen = async () =>
    setIsContactDetailModalOpen(!isContactDetailModalOpen)
  const setPassedLEContactId = useSetLEContactId(
    (state) => state.setPassedLEContactId
  )
  const contacts =
    orderBy(licensedEstablishment.contacts, (item) => item.id, ['asc']) ?? []

  const closeModalOpened = () => {
    setIsContactDetailModalOpen(
      isContactDetailModalOpen ? false : isContactDetailModalOpen
    )
  }

  const putLicensedEstablishmentMutation = usePutLicensedEstablishment({
    licensedEstablishmentId: licensedEstablishment.id,
    onSuccess: () => {
      setMessage('Licensed Establishment has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      closeModalOpened()
    },
    onError: () => {
      setMessage('Error updating Licensed Establishment', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  // Enum of Contact Types used for checking to display the "Add" button or not
  const contactTypes = ['Primary', 'LocationSweepstakes'] as const

  const getColumns = (): GridColDef[] => [
    {
      field: 'contacttype',
      headerName: 'Type',
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => {
        const contactType = (typ: LicensedEstablishmentContactType) => {
          switch (typ) {
            case 'Primary':
              return 'Primary'
            case 'LocationSweepstakes':
              return 'Location Sweepstakes'
            default:
              return ''
          }
        }

        const leContactType = params.row.contactType
        return contactType(leContactType)
      },
    },
    {
      field: 'contact',
      headerName: 'Contact',
      minWidth: 300,
      flex: 1,
      valueGetter: (params) =>
        `${String(params.row.firstName)} ${String(params.row.lastName)}`,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => (params.row.email ? params.row.email : '-'),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      minWidth: 300,
      flex: 1,
      valueGetter: (params) => formatPhoneNumber(params.row.phoneNumber),
    },
    {
      field: '__menu__',
      headerName: '',
      align: 'right',

      renderCell: (params) => {
        // Only display the option menu on the row if the type is not 'Primary'
        return params.row.contactType !== 'Primary' ? (
          <DeleteLEContactTypeRenderCell params={params} />
        ) : null
      },
    },
  ]

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (
      params.field !== '__menu__' &&
      params.id !== null &&
      params.row.contactType !== 'Primary'
    ) {
      setPassedLEContactId(Number(params.id))
      void toggleContactDetailModalIsOpen()
    }
  }

  return (
    <>
      <Box>
        <SectionHeader
          title="Contacts"
          buttonText="Add"
          onClickButton={onClickAdd}
          showButton={contacts.length !== contactTypes.length} // Only show "Add" button if not all contact types are present
        />

        <Box
          border={{ md: `1px solid ${theme.palette.grey[200]}` }}
          borderRadius={'5px'}
          p={{ xs: 0, sm: 3 }}
        >
          <DataTable
            columns={getColumns()}
            rows={contacts}
            onCellClick={selectCell}
          />
        </Box>
      </Box>
      <EditLicensedEstablishmentContactDetails
        licensedEstablishment={licensedEstablishment}
        updateIsActive={putLicensedEstablishmentMutation.isPending}
        isModalOpen={isContactDetailModalOpen}
        toggleModalIsOpen={toggleContactDetailModalIsOpen}
        onSubmitLicensedEstablishment={(licensedEstablishment) => {
          putLicensedEstablishmentMutation.mutate(licensedEstablishment)
        }}
      />
    </>
  )
}
